<template>
	<div class="calendar-widget">
		<span v-for="weekDay in headerDays" class="header-day" :class="headerClass(weekDay)">{{ weekDay.title }}</span>
		<button v-for="calDay in calendarDays" class="cw-day" :class="calClass(calDay)" @click.prevent.stop="pickDay(calDay)"><span>{{ calDay.title }}</span></button>
	</div>
</template>

<script setup lang="ts">
import {parseDate, sameDay, updateIsoDate} from "@t/util/DateUtil";
import {computed} from "vue";
import type {CalendarDay, HeaderDay} from "./CalendarWidgetTypes";
import {weekInfo} from "./CalendarWidgetTypes";

/*****
 * String representation of a month
 *****/


const HEADER_FORMAT = new Intl.DateTimeFormat(undefined, {
	weekday: "short",
});

const DAY_FORMAT = new Intl.DateTimeFormat(undefined, {
	day: "numeric",
});

const props = defineProps({
	// Selected day (in ISO format)
	modelValue: String,
	// What year/month to display
	month: Number,
	year: Number,
});

const emit = defineEmits(["update:modelValue"]);

const monthNum = computed(() => {
	if (typeof props.month === "number") return props.month;
	return new Date().getMonth();
});

const yearNum = computed(() => {
	if (typeof props.year === "number") return props.year;
	return new Date().getFullYear();
});

const selected = computed(() => {
	if (props.modelValue == null || typeof props.modelValue === "undefined") return null;
	const parsed = parseDate(props.modelValue);
	return parsed.date;
});


const headerDays = computed<HeaderDay[]>(() => {
	const wi = weekInfo();
	const dateFD = wi.firstDay % 7; // week info has different numbering of weekdays then Date() class
	const days: HeaderDay[] = [];
	let f = new Date();
	while (f.getDay() !== dateFD) {
		f.setDate(f.getDate() - 1);
	}

	for (let i = 0; i < 7; i++) {
		const wiDay = wi.firstDay + i;
		const isWeekend = wi.weekend.indexOf(wiDay) >= 0;
		days.push({
			title: HEADER_FORMAT.format(f),
			weekend: isWeekend,
		});
		f.setDate(f.getDate() + 1);
	}
	return days;
});

/* Calculate full month of days */
const calendarDays = computed<CalendarDay[]>(() => {
	const current = new Date();
	const f = new Date(yearNum.value, monthNum.value, 1);
	const wi = weekInfo();
	const dateFD = wi.firstDay % 7; // week info has different numbering of weekdays then Date() class
	const days: CalendarDay[] = [];
	while (f.getDay() !== dateFD) {
		f.setDate(f.getDate() - 1); // align
	}
	do {
		for (let i = 0; i < 7; i++) {
			const wiDay = wi.firstDay + i;
			const isWeekend = wi.weekend.indexOf(wiDay) >= 0;
			days.push({
				title: f.getDate().toString(),
				date: new Date(f.getTime()),
				weekend: isWeekend,
				current: sameDay(current, f),
				out: f.getMonth() !== monthNum.value,
				selected: sameDay(selected.value, f),
			});
			f.setDate(f.getDate() + 1);
		}
	} while (f.getMonth() === monthNum.value);
	return days;
});

function headerClass(weekDay: HeaderDay) {
	if (weekDay.weekend) return "header-weekend";
	else return "header-workday";
}

function calClass(calDay: CalendarDay) {
	const clss = [];
	if (calDay.weekend) clss.push("cal-weekend");
	else clss.push("cal-workday");
	if (calDay.current) clss.push("cal-current");
	if (calDay.out) clss.push("cal-out");
	if (calDay.selected) clss.push("cal-selected");
	return clss;
}

function pickDay(calDay: CalendarDay) {
	const formatted = updateIsoDate(props.modelValue, calDay.date);
	console.log("Picked", formatted, calDay);
	emit("update:modelValue", formatted);
}

</script>

<style>
.calendar-widget {
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
	min-width: 224px;
	max-width: 336px;
	color: var(--fg);
	background-color: var(--bg);
}

.calendar-widget {
	--swatch-size: 24px;
}

@media (pointer: coarse) {
	.calendar-widget {
		--swatch-size: 32px;
	}
}


.calendar-widget > span {
	text-align: center;
}

.header-day {
	font-size: var(--font-size-s);
	font-weight: normal;
	text-transform: uppercase;
	height: var(--swatch-size);
	line-height: var(--swatch-size);
	color: var(--fg);
	background-color: var(--bg);
}

.cw-day {
	cursor: pointer;
	border: none;
	padding: 0;
	margin: 0;
	line-height: var(--swatch-size);
	color: var(--fg);
	background-color: transparent;
	border-radius: 0;	/* Cancel button round corners */
}

.cw-day > span {
	display: inline-block;
	border-radius: 50%;
	width: var(--swatch-size);
	height: var(--swatch-size);
	line-height: var(--swatch-size);
	color: var(--fg);
	background-color: var(--bg);
}



.header-weekend {
}

.cal-current {
	font-weight: bold;
}

.cal-selected > span {
	color: white;
	font-weight: bold;
}
</style>
